/**
 * Decide whether an operator login capture actually captured anything (CS-1719,
 * reopen of CS-1717).
 *
 * `browserbase_finish_login` used to report `ok: true` whenever the session
 * reached COMPLETED. COMPLETED means the session ended and Browserbase wrote the
 * browser state back into the Context - it says NOTHING about whether the site's
 * auth cookie was in that state. So a sign-in that silently failed to persist
 * returned a machine-readable success flag that was false, and every caller that
 * branched on `ok` branched the wrong way. A reporter hit this four consecutive
 * times on LinkedIn: fresh session, `li_at` absent, 8 cookies byte-for-byte
 * identical each time, and `finish_login` said `{"ok": true}` every time.
 *
 * This module turns that claim into a measurement. Given the cookie NAMES
 * observed in the login session's browser just before release (see
 * `login-cookie-probe.ts`), it answers one of three things:
 *
 *   confirmed      - the site's known sign-in cookie was present. Capture is real.
 *   not_persisted  - the site's known sign-in cookie was ABSENT. The operator's
 *                    sign-in did not leave a session cookie in this browser, so
 *                    there is nothing to persist and `ok` must be false.
 *   unverified     - we hold no expectation for this site, or the probe could not
 *                    run. We do not know, and must not claim either way.
 *
 * The three-state shape is deliberate. Flipping `ok` to false for every
 * unverifiable capture would regress every site where capture demonstrably works
 * today, and would itself be an assertion unsupported by evidence - the same
 * defect in the opposite direction.
 */

/** A cookie as observed by the probe. Names and domains ONLY - never values. */
export interface ObservedCookie {
  name: string;
  domain: string;
}

export type LoginCaptureVerdict = 'confirmed' | 'not_persisted' | 'unverified';

/**
 * Fixed failure codes. CodeRabbit (#6016): the reason reaches the AGENT in
 * `verification_reason`, and `connectUrl` carries a per-session signing key - so
 * free-form exception text here is a credential-leak path, not just noise.
 * Detail stays in the server-side log.
 */
export type CookieProbeFailureReason =
  | 'no_websocket'
  | 'no_connect_url'
  | 'ws_construct_failed'
  | 'ws_error'
  | 'ws_closed'
  | 'send_failed'
  | 'timeout'
  | 'no_page_target'
  | 'attach_failed'
  | 'get_cookies_error'
  | 'malformed_cookie_payload'
  /** The probe threw despite being documented not to (raised by the caller). */
  | 'probe_threw'
  /** The session had no connect url to probe. */
  | 'not_attempted';


export interface SiteLoginExpectation {
  /** Registrable host the expectation applies to (matches host and subdomains). */
  host: string;
  /** Cookie names that constitute a signed-in session. Any one present = signed in. */
  cookies: string[];
}

/**
 * Sites whose sign-in cookie we are confident about.
 *
 * This list is deliberately SHORT. A wrong entry is worse than a missing one: a
 * missing entry yields `unverified` (today's behaviour, no regression), while a
 * wrong entry yields a false `not_persisted` that breaks a working login flow and
 * sends an operator to sign in again for no reason. Only add a site when the
 * cookie name has been observed, not guessed.
 *
 *   linkedin.com  li_at      - the reported failure, and the reason this exists.
 *   x.com         auth_token - the positive control: capture is known to work here.
 */
export const SITE_LOGIN_COOKIES: readonly SiteLoginExpectation[] = Object.freeze([
  Object.freeze({ host: 'linkedin.com', cookies: Object.freeze(['li_at']) }),
  Object.freeze({ host: 'x.com', cookies: Object.freeze(['auth_token']) }),
  Object.freeze({ host: 'twitter.com', cookies: Object.freeze(['auth_token']) }),
]) as readonly SiteLoginExpectation[];

/** Strip a leading dot so `.www.linkedin.com` and `www.linkedin.com` compare equal. */
function normalizeDomain(domain: string): string {
  const d = domain.trim().toLowerCase();
  return d.startsWith('.') ? d.slice(1) : d;
}

/** True when `domain` is `host` or a subdomain of it. */
function domainMatchesHost(domain: string, host: string): boolean {
  const d = normalizeDomain(domain);
  return d === host || d.endsWith(`.${host}`);
}

/**
 * The sign-in cookie expectation for a site string (a bare domain or a full URL),
 * or null when we hold none. Never throws on malformed input.
 */
export function expectedLoginCookiesFor(
  site: string | null | undefined,
): SiteLoginExpectation | null {
  if (!site) return null;
  const s = site.trim().toLowerCase();
  if (!s) return null;

  let host: string;
  try {
    host = new URL(/^https?:\/\//.test(s) ? s : `https://${s}`).hostname;
  } catch {
    return null;
  }
  if (!host) return null;

  return SITE_LOGIN_COOKIES.find((e) => domainMatchesHost(host, e.host)) ?? null;
}

export interface ClassifyLoginCaptureInput {
  /** The site the operator was asked to sign in to, as recorded at start_login. */
  site: string | null | undefined;
  /**
   * Cookies observed in the login session's browser just before release, or null
   * when the probe could not run (which is NOT evidence of absence).
   */
  observed: ObservedCookie[] | null;
  /**
   * Why the probe failed, when `observed` is null. A FIXED code, never free-form
   * exception text: this value reaches the agent in `verification_reason`, and
   * the session connect url carries a signing key (CodeRabbit, #6016).
   */
  probeFailureReason?: CookieProbeFailureReason;
}

export interface LoginCaptureClassification {
  verdict: LoginCaptureVerdict;
  /** Short machine-readable reason, for logs and the tool response. */
  reason: string;
  /** The cookie names we were looking for, when we held an expectation. */
  expectedCookies?: string[];
}

/**
 * Classify a login capture from what was actually observed in the browser.
 *
 * Absence of evidence is reported as `unverified`, never as `confirmed` and never
 * as `not_persisted`: the whole point of CS-1719 is that a flag must not assert
 * more than the evidence supports.
 */
export function classifyLoginCapture(
  input: ClassifyLoginCaptureInput,
): LoginCaptureClassification {
  const expectation = expectedLoginCookiesFor(input.site);

  // No expectation: we cannot tell a captured login from a failed one for this
  // site, so say so. This is the path every un-listed site takes, and it
  // preserves today's behaviour rather than regressing it.
  if (!expectation) {
    return { verdict: 'unverified', reason: 'no_known_signin_cookie_for_site' };
  }

  // The probe failing tells us nothing about the cookie jar. Do not convert a
  // failed measurement into a negative result.
  if (input.observed === null) {
    return {
      verdict: 'unverified',
      reason: `cookie_probe_failed:${input.probeFailureReason ?? 'unknown'}`,
      expectedCookies: expectation.cookies,
    };
  }

  const present = input.observed.some(
    (c) =>
      expectation.cookies.includes(c.name) && domainMatchesHost(c.domain, expectation.host),
  );

  return present
    ? { verdict: 'confirmed', reason: 'signin_cookie_present', expectedCookies: expectation.cookies }
    : { verdict: 'not_persisted', reason: 'signin_cookie_absent', expectedCookies: expectation.cookies };
}
